import { useState } from 'react';
import { ArrowDownLeft, CreditCard, Building, Smartphone, Wallet } from 'lucide-react';
import { PageShell, FG, INP, SEL, Row2, BtnPrimary, BtnGhost, fmtINR, Spinner, useBanner, useWalletUser } from './WalletShared';
import walletController from '../../controllers/walletController';

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000, 25000];
const METHODS = [
  { value:'upi',           label:'UPI',           icon:Smartphone },
  { value:'card',          label:'Card',           icon:CreditCard },
  { value:'bank_transfer', label:'Bank Transfer',  icon:Building },
  { value:'wallet',        label:'Wallet',         icon:Wallet },
];

const AddMoney = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [form, setForm]     = useState({ amount:'', method:'upi', upiId:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);

  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const handleSubmit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return notify('error','Please enter a valid amount');
    if (!form.method) return notify('error','Please select a payment method');
    setSaving(true);
    const res = await walletController.addMoney(userId, {
      amount: Number(form.amount), method: form.method,
      upiId: form.upiId, notes: form.notes,
    });
    setSaving(false);
    if (res.success) {
      setSuccess({ amount: Number(form.amount), method: form.method });
      setForm({ amount:'', method:'upi', upiId:'', notes:'' });
      notify('success', res.message);
    } else notify('error', res.message);
  };

  return (
    <PageShell title="Add Money" subtitle="Top up your wallet balance" icon={ArrowDownLeft}
      banner={banner} onCloseBanner={clearBanner}>

      <div style={{ maxWidth:540, margin:'0 auto' }}>
        {success && (
          <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:12, padding:'18px 20px', marginBottom:20, textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:700, color:'#15803d', marginBottom:4 }}>{fmtINR(success.amount)}</div>
            <div style={{ fontSize:14, color:'#15803d' }}>Added successfully via {success.method.replace('_',' ').toUpperCase()}</div>
          </div>
        )}

        <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f1f5f9', overflow:'hidden' }}>
          <div style={{ padding:'18px 20px', borderBottom:'1px solid #f1f5f9' }}>
            <span style={{ fontSize:15, fontWeight:700, color:'#1e293b' }}>Enter Amount</span>
          </div>
          <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:18 }}>
            {/* Amount input */}
            <FG label="Amount (₹) *">
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:16, fontWeight:600, color:'#64748b' }}>₹</span>
                <INP type="number" min="1" value={form.amount} onChange={e => f('amount',e.target.value)}
                  placeholder="Enter amount" style={{ paddingLeft:28, fontSize:22, fontWeight:700, height:52 }}/>
              </div>
            </FG>

            {/* Quick amounts */}
            <div>
              <div style={{ fontSize:11, color:'#64748b', marginBottom:8, fontWeight:600, textTransform:'uppercase' }}>Quick Select</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {QUICK_AMOUNTS.map(a => (
                  <button key={a} onClick={() => f('amount', String(a))}
                    style={{ padding:'6px 14px', border:`2px solid ${form.amount==a?'#3b82f6':'#e2e8f0'}`, borderRadius:8, background:form.amount==a?'#dbeafe':'#fff', color:form.amount==a?'#1d4ed8':'#475569', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                    ₹{a.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment method */}
            <FG label="Payment Method *">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {METHODS.map(({ value, label, icon:Icon }) => (
                  <button key={value} onClick={() => f('method',value)}
                    style={{ padding:'10px 12px', border:`2px solid ${form.method===value?'#3b82f6':'#e2e8f0'}`, borderRadius:10, background:form.method===value?'#dbeafe':'#fff', color:form.method===value?'#1d4ed8':'#475569', cursor:'pointer', display:'flex', alignItems:'center', gap:8, fontSize:13, fontWeight:500 }}>
                    <Icon size={15}/>{label}
                  </button>
                ))}
              </div>
            </FG>

            {form.method === 'upi' && (
              <FG label="UPI ID"><INP value={form.upiId} onChange={e => f('upiId',e.target.value)} placeholder="yourname@upi"/></FG>
            )}

            <FG label="Notes (optional)">
              <INP value={form.notes} onChange={e => f('notes',e.target.value)} placeholder="Purpose of top-up"/>
            </FG>

            {/* Summary */}
            {form.amount > 0 && (
              <div style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px', fontSize:13 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ color:'#64748b' }}>Amount</span><span>{fmtINR(Number(form.amount))}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, borderTop:'1px solid #e2e8f0', paddingTop:8, marginTop:4 }}>
                  <span>You will receive</span><span style={{ color:'#15803d' }}>{fmtINR(Number(form.amount))}</span>
                </div>
              </div>
            )}

            <BtnPrimary onClick={handleSubmit} disabled={saving} style={{ justifyContent:'center', padding:'12px' }}>
              {saving ? <><Spinner size={14}/>Processing…</> : <><ArrowDownLeft size={14}/>Add {form.amount?fmtINR(Number(form.amount)):''} to Wallet</>}
            </BtnPrimary>
          </div>
        </div>
      </div>
    </PageShell>
  );
};
export default AddMoney;